// hooks/useEmargement.js
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';

/**
 * Hook personnalisé pour gérer toutes les opérations liées aux émargements
 * @returns {Object} - Fonctions et états pour gérer les émargements
 */
export const useEmargement = () => {
  // États pour stocker les données
  const [apprentis, setApprentis] = useState([]);
  const [formateurs, setFormateurs] = useState([]);
  const [emargements, setEmargements] = useState([]);
  const [sessions, setSessions] = useState(['CQP TAVA 2024/2026', 'CQP TAVA 2025/2027']);
  
  // États pour gérer le chargement et les erreurs
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Récupère toutes les données nécessaires depuis Supabase
   */
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log("Récupération des données depuis Supabase...");
      
      // Récupérer les apprentis
      const { data: apprentisData, error: apprentisError } = await supabase
        .from('apprentis')
        .select('*')
        .eq('actif', true)
        .order('nom');
      
      if (apprentisError) throw apprentisError;
      console.log("Apprentis récupérés:", apprentisData?.length || 0);
      
      // Récupérer les formateurs
      const { data: formateursData, error: formateursError } = await supabase
        .from('formateurs')
        .select('*')
        .eq('actif', true)
        .order('nom');
      
      if (formateursError) throw formateursError;
      console.log("Formateurs récupérés:", formateursData?.length || 0);
      
      // Récupérer les émargements
      const { data: emargementsData, error: emargementsError } = await supabase
        .from('emargements')
        .select('*')
        .order('date', { ascending: false });
      
      if (emargementsError) throw emargementsError;
      console.log("Émargements récupérés:", emargementsData?.length || 0);
      
      // Récupérer les sessions
      const { data: sessionsData, error: sessionsError } = await supabase
        .from('sessions')
        .select('nom')
        .eq('actif', true)
        .order('date_debut');
      
      if (sessionsError) throw sessionsError;
      
      // Mettre à jour les états avec les données récupérées
      setApprentis(apprentisData || []);
      setFormateurs(formateursData || []);
      setEmargements(emargementsData || []);
      
      if (sessionsData && sessionsData.length > 0) {
        setSessions(sessionsData.map(session => session.nom));
        console.log("Sessions récupérées:", sessionsData.length);
      }
    } catch (err) {
      console.error('Erreur lors du chargement des données:', err);
      setError(err.message || 'Une erreur est survenue lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, []);

  // Charger les données au montage du composant
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /**
   * Enregistre un nouvel émargement dans Supabase
   * @param {Object} emargementData - Données de l'émargement à enregistrer
   * @returns {Object} - L'émargement créé
   */
  const saveEmargement = async (emargementData) => {
    try {
      setLoading(true);
      
      // Ne garder que les formateurs qui ont signé
      const formateursSignes = [];
      
      // Parcourir toutes les signatures
      Object.keys(emargementData.signatures || {}).forEach(key => {
        if (key.startsWith('formateur_') && emargementData.signatures[key]) {
          const formateurId = key.split('_')[1]; // Extraire l'ID du formateur
          if (!formateursSignes.includes(formateurId)) {
            formateursSignes.push(formateurId);
          }
        }
      });
      
      console.log("Formateurs ayant signé:", formateursSignes);
      
      const { data, error } = await supabase
        .from('emargements')
        .insert([{
          date: emargementData.date,
          session: emargementData.session,
          signatures: emargementData.signatures,
          securisation_parcours: emargementData.securisationParcours,
          apprentis_ids: emargementData.apprentisIds,
          apprentis_absents_ids: emargementData.apprentisAbsentsIds || [], // Pour gérer les absences
          formateurs_ids: formateursSignes, // Uniquement ceux qui ont signé
          commentaire: emargementData.commentaire || ''
        }])
        .select();
      
      if (error) throw error;
      
      console.log("Émargement enregistré avec succès:", data[0].id);
      
      // Ajouter le nouvel émargement à la liste locale
      setEmargements(prevEmargements => [data[0], ...prevEmargements]);
      
      return data[0];
    } catch (err) {
      console.error('Erreur lors de l\'enregistrement de l\'émargement:', err);
      setError(err.message || 'Une erreur est survenue lors de l\'enregistrement');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Met à jour l'URL du PDF d'un émargement
   * @param {string} id - ID de l'émargement
   * @param {string} pdfUrl - URL du PDF généré
   */
  const updateEmargementWithPdf = async (id, pdfUrl) => {
    try {
      setLoading(true);
      
      console.log("Mise à jour de l'émargement avec l'URL du PDF:", id);
      console.log("URL du PDF:", pdfUrl);
      
      const { data, error } = await supabase
        .from('emargements')
        .update({ pdf_url: pdfUrl })
        .eq('id', id)
        .select();
      
      if (error) throw error;
      
      console.log("Émargement mis à jour avec succès");
      
      // Mettre à jour l'émargement dans la liste locale
      setEmargements(prevEmargements => 
        prevEmargements.map(emargement => 
          emargement.id === id ? { ...emargement, pdf_url: pdfUrl } : emargement
        )
      );
      
      return data[0];
    } catch (err) {
      console.error('Erreur lors de la mise à jour du PDF:', err);
      setError(err.message || 'Une erreur est survenue lors de la mise à jour du PDF');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Supprime un émargement et son PDF associé
   * @param {string} id - ID de l'émargement à supprimer
   */
  const removeEmargement = async (id) => {
    try {
      setLoading(true);
      
      console.log("Suppression de l'émargement:", id);
      
      // Récupérer d'abord l'émargement pour obtenir l'URL du PDF
      const emargementToDelete = emargements.find(e => e.id === id);
      
      if (emargementToDelete?.pdf_url) {
        // Extraire le chemin du fichier depuis l'URL du PDF
        const url = new URL(emargementToDelete.pdf_url);
        const pathMatch = url.pathname.match(/\/emargements-pdf\/(.+)$/);
        
        if (pathMatch && pathMatch[1]) {
          const filePath = decodeURIComponent(pathMatch[1]);
          
          console.log("Suppression du fichier PDF:", filePath);
          
          // Supprimer le fichier du storage
          const { error: storageError } = await supabase.storage
            .from('emargements-pdf')
            .remove([filePath]);
          
          if (storageError) {
            console.error('Erreur lors de la suppression du fichier PDF:', storageError);
          }
        }
      }
      
      // Supprimer l'émargement de la base de données
      const { error } = await supabase
        .from('emargements')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      console.log("Émargement supprimé avec succès");
      
      // Supprimer l'émargement de la liste locale
      setEmargements(prevEmargements => 
        prevEmargements.filter(emargement => emargement.id !== id)
      );
      
      return true;
    } catch (err) {
      console.error('Erreur lors de la suppression de l\'émargement:', err);
      setError(err.message || 'Une erreur est survenue lors de la suppression');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Télécharge un fichier PDF sur Supabase Storage
   * @param {File} file - Fichier PDF à télécharger
   * @param {string} path - Chemin de destination dans le bucket
   * @returns {string} - URL publique du fichier téléchargé
   */
  const uploadPdf = async (file, path) => {
    try {
      console.log("Téléchargement du fichier PDF:", path);
      console.log("Taille du fichier:", file.size, "bytes");
      
      // Télécharger le fichier sur Supabase Storage
      const { data, error } = await supabase.storage
        .from('emargements-pdf')
        .upload(path, file, {
          cacheControl: '3600',
          upsert: true // Remplacer si existe déjà
        });
      
      if (error) {
        console.error("Erreur lors du téléchargement du PDF:", error);
        throw error;
      }
      
      console.log("Fichier PDF téléchargé avec succès");
      
      // Récupérer l'URL publique du fichier
      const { data: publicUrlData } = supabase.storage
        .from('emargements-pdf')
        .getPublicUrl(path);
      
      console.log("URL publique du PDF:", publicUrlData.publicUrl);
      
      return publicUrlData.publicUrl;
    } catch (err) {
      console.error('Erreur lors du téléchargement du PDF:', err);
      throw err;
    }
  };

  /**
   * Génère et télécharge un PDF à partir d'un élément HTML
   * @param {React.RefObject} elementRef - Référence à l'élément HTML
   * @param {string} emargementId - ID de l'émargement
   * @param {string} fileName - Nom du fichier
   * @returns {string} - URL du PDF généré
   */
  const generateAndUploadPdf = async (elementRef, emargementId, fileName) => {
    try {
      console.log("Génération du PDF pour l'émargement:", emargementId);
      
      // Options pour html2canvas
      const options = {
        backgroundColor: '#1e293b',
        scale: 2,
        useCORS: true,
        logging: true,
      };
      
      // Importer html2canvas et jsPDF dynamiquement
      const html2canvas = (await import('html2canvas')).default;
      const { jsPDF } = await import('jspdf');
      
      if (!elementRef.current) {
        throw new Error("La référence à l'élément HTML est nulle");
      }
      
      console.log("Capture de l'élément HTML en canvas...");
      
      // Capturer l'élément en canvas
      const canvas = await html2canvas(elementRef.current, options);
      const imgData = canvas.toDataURL('image/png');
      
      console.log("Canvas généré avec succès");
      
      // Créer un PDF
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });
      
      // Dimensions du PDF
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      // Dimensions de l'image
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 10;
      
      console.log("Ajout de l'image au PDF...");
      
      // Ajouter l'image au PDF
      pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
      
      console.log("Génération du blob PDF...");
      
      // Générer le blob PDF
      const pdfBlob = pdf.output('blob');
      
      console.log("Blob PDF généré:", pdfBlob.size, "bytes");
      
      // Créer un objet File
      const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });
      
      // Chemin dans le bucket
      const filePath = `${emargementId}/${fileName}`;
      
      console.log("Téléchargement du PDF sur Supabase Storage...");
      
      // Télécharger le PDF
      const pdfUrl = await uploadPdf(pdfFile, filePath);
      
      console.log("Mise à jour de l'émargement avec l'URL du PDF...");
      
      // Mettre à jour l'émargement avec l'URL du PDF
      await updateEmargementWithPdf(emargementId, pdfUrl);
      
      console.log("PDF généré et téléchargé avec succès:", pdfUrl);
      
      return pdfUrl;
    } catch (err) {
      console.error('Erreur lors de la génération du PDF:', err);
      throw err;
    }
  };

  /**
   * Filtre les émargements par texte de recherche
   * @param {string} searchTerm - Terme de recherche
   * @returns {Array} - Émargements filtrés
   */
  const filterEmargements = (searchTerm) => {
    if (!searchTerm || !emargements) return emargements;
    
    return emargements.filter(emargement => {
      const dateFormatted = new Date(emargement.date).toLocaleDateString('fr-FR');
      
      return (
        emargement.session?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        dateFormatted.includes(searchTerm) ||
        emargement.date?.includes(searchTerm)
      );
    });
  };

  // Retourner toutes les fonctions et états nécessaires
  return {
    // Données
    apprentis,
    formateurs,
    emargements,
    sessions,
    
    // États
    loading,
    error,
    
    // Fonctions
    fetchData,
    saveEmargement,
    updateEmargementWithPdf,
    removeEmargement,
    uploadPdf,
    generateAndUploadPdf,
    filterEmargements,
    
    // Réinitialiser l'erreur
    clearError: () => setError(null)
  };
};

export default useEmargement;